import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import { APICommunicator } from '~/api/APICommunicator';
import { toastChatCommandError } from '~/features/whatsapp/utils/toastChatCommandError';
import { queryKeys } from '~/utils/queryKeys';

type SendTemplateMessagePayload = {
    divisionId: string;
    templateName: string;
    templateLanguage: string;
    // Only for positional-style templates — backend rejects it for named-style templates.
    templateParameters?: Array<string>;
    // A POSITIONAL header's {{1}} is its own slot, numbered independently of the body's.
    templateHeaderParameters?: Array<string>;
    loanRequestId?: string;
    // Required for `source: 'custom'` vars; also used to override agent/applicant-sourced named vars.
    customTemplateValues?: Record<string, string>;
    // HEADER named-slot overrides — a name shared with the body's slot can hold a different value.
    headerCustomTemplateValues?: Record<string, string>;
};

// No 24h-window guard — sendTemplateMessage is Meta's mechanism for re-opening the window.
export const useSendTemplateMessageMutation = (chatId: string) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: (payload: SendTemplateMessagePayload) =>
            APICommunicator.sendCommand({
                aggregateId: chatId,
                aggregateName: AGGREGATE_NAMES.CHAT,
                commandName: CHAT_COMMANDS.sendTemplateMessage,
                payload,
            }),
        onError: (error) => {
            toastChatCommandError(error, t, 'WHATSAPP_CHAT.ERROR.SEND_TEMPLATE_FAILED');
        },
        onSuccess: () => {
            // Skip useChainAwareInvalidation — the chain includes manta's Meta round-trip, which would delay the queued bubble.
            queryClient.invalidateQueries({ queryKey: queryKeys.getChatMessages(chatId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
        },
    });
};
